/**
 * Renders the single information-block image the worker has mounted on the
 * respondent's screen during a live conversation (feature #187), styled as a
 * speaker tile that sits beside hollie's orb on desktop and above it on a phone.
 *
 * Presentational and driven entirely by the `block` prop: the mount/unmount
 * state lives in {@link useInformationBlockMount}, and the worker (the Durable
 * Object) is the authority on what is mounted. The component never sends a
 * client message: on reconnect the DO re-mounts authoritatively. On image load
 * it reports the natural aspect ratio so the host panel can size itself (wider
 * for a landscape image, narrower for a portrait one).
 *
 * @module components/call-screen/InformationBlockMount
 */

import { useEffect, useState } from 'react';
import { IconPlayerPauseFilled, IconPlayerPlayFilled } from '@tabler/icons-react';
import type { MountedInformationBlock } from './hooks/useInformationBlockMount';
import { KlipyAttribution } from '../klipy/KlipyAttribution';

/** Props for {@link InformationBlockMount}. */
export interface InformationBlockMountProps {
  /** The currently-mounted block, or null when nothing is on screen. */
  block: (Omit<MountedInformationBlock, 'fieldName'> & { fieldName?: string }) | null;
  /** Reports the image's natural width/height ratio once it loads. */
  onAspectRatio?: (ratio: number) => void;
  /** Signals decoded image availability; the host still verifies screen visibility. */
  onImageReady?: () => void;
  /** Allows the host to offer a retry without acknowledging unseen content. */
  onImageError?: () => void;
}

/**
 * Show the currently-mounted information-block image as a tile, or nothing when
 * there is none. On an image load error it shows a fallback and suppresses the
 * lightbox affordance rather than collapsing to a zero-height row.
 */
export function InformationBlockMount({ block, onAspectRatio, onImageReady, onImageError }: InformationBlockMountProps) {
  const [errored, setErrored] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [animationPaused, setAnimationPaused] = useState(false);

  // Reset the error and lightbox whenever the mounted block changes.
  useEffect(() => {
    setErrored(false);
    setLightboxOpen(false);
    setAnimationPaused(
      block?.imageSource === 'klipy'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
  }, [block?.fieldName, block?.imageUrl]);

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  if (!block) return null;

  const label = block.title && block.title.trim().length > 0 ? block.title : 'shared image';
  const canPause = block.imageSource === 'klipy' && Boolean(block.imageStillUrl);
  const activeImageUrl = canPause && animationPaused
    ? block.imageStillUrl!
    : block.imageUrl;

  if (errored) {
    return (
      <div
        data-testid="information-block-mount"
        className="flex h-full w-full items-center justify-center rounded-xl border border-border bg-surface-raised p-4 text-sm text-muted-foreground"
      >
        Couldn&apos;t load preview
      </div>
    );
  }

  return (
    <div
      data-testid="information-block-mount"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-background"
    >
      <button
        type="button"
        aria-label={`Enlarge ${label}`}
        className="flex h-full w-full items-center justify-center focus:outline-none focus:ring-2 focus:ring-ring"
        onClick={() => setLightboxOpen(true)}
      >
        <img
          src={activeImageUrl}
          alt={label}
          className="max-h-full max-w-full object-contain"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalHeight > 0) onAspectRatio?.(img.naturalWidth / img.naturalHeight);
            onImageReady?.();
          }}
          onError={() => { setErrored(true); onImageError?.(); }}
        />
      </button>

      <span className="absolute bottom-3 left-3 rounded-lg bg-black/40 px-2.5 py-1 text-sm font-medium text-white backdrop-blur-sm">
        {label}
      </span>

      {(block.imageSource === 'klipy' || canPause) && (
        <div role="group" aria-label="GIF controls" className="absolute bottom-3 right-3 z-10 flex items-center gap-2">
          {block.imageSource === 'klipy' && <KlipyAttribution />}
          {canPause && <button
            type="button"
            aria-label={animationPaused ? 'Play GIF' : 'Pause GIF'}
            onClick={() => setAnimationPaused((paused) => !paused)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur-sm transition hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {animationPaused
              ? <IconPlayerPlayFilled aria-hidden="true" size={15} />
              : <IconPlayerPauseFilled aria-hidden="true" size={15} />}
          </button>}
        </div>
      )}

      {lightboxOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          data-testid="information-block-lightbox"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setLightboxOpen(false)}
        >
          <img
            src={activeImageUrl}
            alt={label}
            className="max-h-full max-w-full object-contain"
            onError={() => setErrored(true)}
          />
        </div>
      )}
    </div>
  );
}
